import { useEffect } from 'react'
import { GetServerSidePropsContext } from 'next'
import dynamic from 'next/dynamic'
import type { GrowthCategory, MarketingSection, Product } from '@mc/api'
import { StaticDataProvider } from '@mc/static-data-react'
import { TRACK_PROPS } from '@mc/track-event'
import { TrackingParamsProvider } from '@mc/tracking-context'
import { RegularFooter } from 'components/Footer/RegularFooter'
import { CoreNavigation } from 'components/Navigation/consumer'
import { trackPen7647BackendEV } from 'experiments/pen-7647_v1_gift_hero_card/server'
import {
  getDefaultProduct,
  getFreeTrialByToken,
  getMarketingRows,
  getV2Categories,
} from 'libs/server/api'
import { getPageData } from 'libs/server/page-data'
import { BelowTheFoldPlaceholder } from './components/BelowTheFold/BelowTheFoldPlaceholder'
import { Hero } from './components/Hero'
import { Meta } from './components/Meta'
import { PageBackground } from './components/PageBackground'
import { trackGiftProductViewed } from './events'
import { TrackingAttributes } from './tracking'

const BelowTheFold = dynamic(() => import('./components/BelowTheFold'), {
  ssr: false,
  loading: () => <BelowTheFoldPlaceholder />,
})

interface GiftProps {
  annualPass?: Product
  categories?: GrowthCategory[]
  marketingRows?: MarketingSection[]
}

const Gift = ({ annualPass, categories, marketingRows }: GiftProps) => {
  useEffect(() => {
    if (annualPass) {
      trackGiftProductViewed(annualPass)
    }
  }, [annualPass])

  return (
    <StaticDataProvider value={{ annualPass, categories, marketingRows }}>
      <TrackingParamsProvider value={{ [TRACK_PROPS.PAGE_TYPE]: 'next-gift' }}>
        <TrackingAttributes page_source='Gift' category='Gifts Landing Page'>
          <Meta />
          <PageBackground>
            <Hero />
            <BelowTheFold />
          </PageBackground>
        </TrackingAttributes>
      </TrackingParamsProvider>
    </StaticDataProvider>
  )
}

Gift.pageSettings = {
  rumLabel: 'Gift',
  analyticsLabel: 'Gifting Landing',
  navigation: CoreNavigation,
  footer: RegularFooter,
}

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  const {
    req: {
      cookies: { referral_token: referralToken },
    },
  } = ctx

  await trackPen7647BackendEV(ctx)

  return getPageData({
    nonCriticalPromises: {
      annualPass: getDefaultProduct(ctx),
      trial: getFreeTrialByToken(ctx, { referralToken }),
      marketingRows: getMarketingRows(ctx),
      categories: getV2Categories(ctx),
    },
  })
}

export default Gift
